import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SettingsProvider } from './contexts/SettingsContext';
import { Toaster } from './components/ui/sonner';
import Login from './pages/Login';
import LoginEnhanced from './pages/LoginEnhanced';
import PersonaSelector from './pages/PersonaSelector';
import PersonaSelectorEnhanced from './pages/PersonaSelectorEnhanced';
import PatientView from './pages/PatientView';
import ReceptionistView from './pages/ReceptionistView';
import DoctorView from './pages/DoctorView';
import BillingView from './pages/BillingView';
import OwnerView from './pages/OwnerView';
import DisplayView from './pages/DisplayView';
import DesignTest from './pages/DesignTest';
import '@/App.css';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-primary rounded-[var(--radius-xl)] p-8">
          <p className="text-label" style={{ color: 'var(--color-text-primary)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppRoutes() {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="glass-primary rounded-[var(--radius-xl)] p-8">
          <p className="text-label" style={{ color: 'var(--color-text-primary)' }}>
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route 
        path="/login" 
        element={isAuthenticated ? <Navigate to="/personas" replace /> : <LoginEnhanced />} 
      />
      <Route 
        path="/login-simple" 
        element={isAuthenticated ? <Navigate to="/personas" replace /> : <Login />} 
      />
      <Route 
        path="/personas" 
        element={
          <ProtectedRoute>
            <PersonaSelectorEnhanced />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/personas-simple" 
        element={
          <ProtectedRoute>
            <PersonaSelector />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/patient" 
        element={
          <ProtectedRoute>
            <PatientView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/receptionist" 
        element={
          <ProtectedRoute>
            <ReceptionistView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/doctor" 
        element={
          <ProtectedRoute>
            <DoctorView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/billing" 
        element={
          <ProtectedRoute>
            <BillingView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/owner" 
        element={
          <ProtectedRoute>
            <OwnerView />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/display" 
        element={<DisplayView />} 
      />
      <Route path="/design-test" element={<DesignTest />} />
      <Route 
        path="/" 
        element={<Navigate to={isAuthenticated ? "/personas" : "/login"} replace />} 
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster 
            position="top-right" 
            expand={false}
            toastOptions={{
              classNames: {
                toast: 'fora-toast',
                success: 'fora-toast-success',
                error: 'fora-toast-error',
                info: 'fora-toast-info',
              }
            }}
          />
        </BrowserRouter>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
